import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import scraperApi from "api";
import {
  AiparserCreations,
  AnalyticsDomainResponseItem,
  ChartsData,
  ErrorLogEntry,
  OverviewStats,
  OverviewStatsSummary,
  DomainTableRow,
} from "utils/analytics/types";
import { buildAnalyticsApiParams } from "utils/analytics/filters";
import {
  mapGraphData,
  mapOverviewStats,
  mapDomainListToTableData,
} from "utils/analytics/mapping";
import { useAnalyticsFilters } from "./AnalyticsFilterProvider";
import { useAbortController } from "hooks/useAbortController";
import { useLoading } from "hooks/useLoading";
import { useToast } from "hooks/useToast";

interface OverviewDataContextType {
  chartData: ChartsData[];
  overviewErrorLogs: ErrorLogEntry[];
  aiparserCreations: AiparserCreations | null;
  isOverviewLoading: boolean;
  error: string | null;
  refetchOverview: () => Promise<void>;
  lastUpdatedTimestamp: Date | null;
  overviewStatsSummary: OverviewStatsSummary | null;
  overviewDomainTableData: DomainTableRow[];
}

const OverviewDataContext = createContext<OverviewDataContextType | undefined>(
  undefined
);

export const OverviewDataProvider = ({ children }: { children: ReactNode }) => {
  const { dateRange, filters, setFilterOptions } = useAnalyticsFilters();
  const { showError } = useToast();

  const {
    createNewSignal: createNewOverviewSignal,
    shouldIgnoreError: shouldIgnoreOverviewError,
  } = useAbortController();

  const { loading: isOverviewLoading, setLoading: setIsOverviewLoading } =
    useLoading();

  const [chartData, setChartData] = useState<ChartsData[]>([]);
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(
    null
  );
  const [overviewDomains, setOverviewDomains] = useState<
    AnalyticsDomainResponseItem[]
  >([]);
  const [overviewErrorLogs, setOverviewErrorLogs] = useState<ErrorLogEntry[]>(
    []
  );
  const [aiparserCreations, setAiparserCreations] =
    useState<AiparserCreations | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedTimestamp, setLastUpdatedTimestamp] = useState<Date | null>(
    null
  );

  const overviewStatsSummary = useMemo(
    () => mapOverviewStats(overviewStats, aiparserCreations),
    [overviewStats, aiparserCreations]
  );

  const overviewDomainTableData = useMemo(
    () => mapDomainListToTableData(overviewDomains),
    [overviewDomains]
  );

  const fetchOverviewData = useCallback(async () => {
    const [start, end] = dateRange;
    if (!start || !end) return;

    const signal = createNewOverviewSignal();

    try {
      setIsOverviewLoading(true);
      setError(null);

      const apiParams = buildAnalyticsApiParams([start, end], filters);
      const response = await scraperApi.analytics.getOverviewData(apiParams, {
        signal,
      });

      if (signal.aborted) return;

      const data = response?.data;

      if (!data) {
        setOverviewStats(null);
        setChartData([]);
        setOverviewDomains([]);
        setOverviewErrorLogs([]);
        setAiparserCreations(null);
        setIsOverviewLoading(false);
        return;
      }

      setOverviewStats(data.stats || null);
      setChartData(mapGraphData(data.graph || []));
      setOverviewDomains(data.domains || []);
      setOverviewErrorLogs(data.errors || []);
      setAiparserCreations(data.aiparserCreations ?? null);
      setLastUpdatedTimestamp(new Date());

      if (data.availableFilters) {
        setFilterOptions(data.availableFilters);
      }
    } catch (err: any) {
      if (shouldIgnoreOverviewError(err)) return;

      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch analytics data";
      setError(errorMessage);
      showError(
        "Failed to load analytics",
        "There was an error loading your analytics data. Please try again."
      );
    } finally {
      if (!signal.aborted) {
        setIsOverviewLoading(false);
      }
    }
  }, [
    dateRange,
    filters,
    shouldIgnoreOverviewError,
    createNewOverviewSignal,
    showError,
    setFilterOptions,
    setIsOverviewLoading,
  ]);

  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  const value = useMemo<OverviewDataContextType>(
    () => ({
      chartData,
      overviewErrorLogs,
      aiparserCreations,
      isOverviewLoading,
      error,
      refetchOverview: fetchOverviewData,
      lastUpdatedTimestamp,
      overviewStatsSummary,
      overviewDomainTableData,
    }),
    [
      chartData,
      overviewErrorLogs,
      aiparserCreations,
      isOverviewLoading,
      error,
      fetchOverviewData,
      lastUpdatedTimestamp,
      overviewStatsSummary,
      overviewDomainTableData,
    ]
  );

  return (
    <OverviewDataContext.Provider value={value}>
      {children}
    </OverviewDataContext.Provider>
  );
};

export const useOverviewData = () => {
  const context = useContext(OverviewDataContext);
  if (context === undefined) {
    throw new Error(
      "useOverviewData must be used within an OverviewDataProvider"
    );
  }
  return context;
};
